import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { GlobalIcon, IconImage } from 'src/modules/global';
import styles from './LayoutOverlay.module.scss';

interface LayoutOverlayProps {
  display?: boolean;
  onClose: () => void;
  showClose?: boolean;
  className?: string;
  containerClassName?: string;
  title?: string;
  error?: string;
  children?: React.ReactNode;
}

function LayoutOverlay({
  children,
  display = false,
  onClose,
  showClose = true,
  className = '',
  containerClassName = '',
  title = '',
  error,
}: LayoutOverlayProps) {
  const isOpen = useRef(false);

  const toggleOverlay = (forceClose = false) => {
    const doOpen = !forceClose && display;

    if (isOpen.current !== doOpen) {
      if (doOpen) {
        LayoutOverlay.openCount++;
      } else if (LayoutOverlay.openCount > 0) {
        LayoutOverlay.openCount--;
      }

      isOpen.current = doOpen;
    }

    // All LayoutOverlays are closed -> show body scrollbars
    if (LayoutOverlay.openCount < 1) {
      document.body.style.overflow = 'unset';
    }
    // One ore more LayoutOverlays are opened -> hide body scrollbars
    else {
      document.body.style.overflow = 'hidden';
    }
  };

  useEffect(() => {
    return () => {
      if (LayoutOverlay.openCount > 0) {
        LayoutOverlay.openCount--;
      }
    };
  }, []);

  // If an overlay is visible, prevent scrolling
  useEffect(toggleOverlay, [display]);

  return display ? (
    <div className={classnames(styles.container, containerClassName)}>
      <div className={classnames(styles.overlay, className)}>
        <div className={styles.top}>
          {title && (
            <div className={styles.title}>
              <h4>{title}</h4>
            </div>
          )}
          {showClose && (
            <div className={styles.closeContainer}>
              <GlobalIcon
                image={IconImage.Close}
                size="fluid"
                className={styles.close}
                onClick={() => {
                  toggleOverlay(true);
                  onClose();
                }}
              />
            </div>
          )}
          {error && <div className={styles.error}>{error}</div>}
        </div>

        {children}
      </div>
    </div>
  ) : null;
}

/** Keep track of how many LayoutOverlay instances are opened */
LayoutOverlay.openCount = 0;

export default LayoutOverlay;
